import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { api } from "../lib/api";
import VimeoPlayer from "../components/VimeoPlayer";
import { SpadeMark } from "../components/LogoLoader";
import Reveal from "../components/Reveal";
import TypographyOverride from "../components/TypographyOverride";
import { normalizeVimeoEmbed } from "../lib/vimeo";
import VimeoPlayerSDK from "@vimeo/player";
import { useAudioMute } from "../lib/audioMute";
import StructuredData, { creativeWorkLD, breadcrumbLD } from "../components/StructuredData";
import { track, useScrollCompletion } from "../lib/analytics";

/**
 * Single component that handles BOTH the teaser (`/work/:slug`)
 * and the detailed case study (`/work/:slug/case-study`).
 *
 * Hero + meta block + next-project are shared.
 * Detailed mode renders the 10 storytelling sections under the meta block.
 */
export default function CaseStudy({ settings, detailed = false }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [work, setWork] = useState(null);
  const [next, setNext] = useState(null);
  const [error, setError] = useState(false);

  const { muted: globalMuted } = useAudioMute();
  const heroRef = useRef(null);
  const heroPlayerRef = useRef(null);
  // Page-wide progress (for the detailed-mode top progress bar).
  const { scrollYProgress } = useScroll();
  // Path A: hero opacity + scale animations REMOVED to eliminate the
  // per-frame Framer Motion scroll subscription that was causing the
  // Safari trackpad stutter on Mac. Audio fade (IO-only, in VimeoHero
  // below) and the video's own visibility handling still fully work.
  // (Path A note above already covers why scale + opacity were removed.)
  // Audio fade is handled inside VimeoHero via its own three-way trigger
  // stack (IO + rAF + scroll/touchmove/wheel) — reliable under Lenis and
  // mobile touch scroll. We keep heroPlayerRef only for potential future
  // imperative control (not currently used).

  useEffect(() => {
    let cancelled = false;
    setWork(null);
    setError(false);
    window.scrollTo(0, 0);
    api
      .get(`/works/by-slug/${slug}`)
      .then(({ data }) => {
        if (cancelled) return;
        setWork(data);
        track("case_study_viewed", {
          slug: data?.slug || slug,
          title: data?.title,
          category: data?.category,
          detailed,
        });
        api.get("/works").then(({ data: all }) => {
          if (cancelled) return;
          const idx = all.findIndex((w) => w.slug === slug);
          if (idx >= 0) setNext(all[(idx + 1) % all.length]);
        });
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [slug, detailed]);

  const pageRef = useRef(null);
  useScrollCompletion(pageRef, 0.8, () => {
    track("case_study_completed", {
      slug: work?.slug || slug,
      title: work?.title,
      detailed,
    });
  });

  useEffect(() => {
    if (work?.title) {
      document.title = `${work.title} — ${detailed ? "Case study" : "Project"}`;
    }
  }, [work, detailed]);

  if (error) {
    return (
      <main className="min-h-[80vh] flex flex-col items-center justify-center px-6 text-center">
        <p className="overline mb-4">404</p>
        <h1 className="font-display text-4xl md:text-6xl tracking-tighter mb-8">
          Project not found.
        </h1>
        <button
          onClick={() => navigate("/work")}
          data-testid="case-study-404-back"
          className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-6 py-3 transition-all"
        >
          Back to all work
        </button>
      </main>
    );
  }

  if (!work) {
    return (
      <main className="min-h-[80vh] flex items-center justify-center">
        <p className="overline text-[#8A8A93]">Loading…</p>
      </main>
    );
  }

  // Defaults (so existing records that have `null` for new fields still render)
  const w = applyDefaults(work);
  const services = (w.services || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const metaItems = [
    w.meta_client_visible && w.client && {
      key: "client",
      label: w.meta_client_label || "Client",
      value: w.client,
      gold: true,
    },
    w.meta_year_visible && w.year && {
      key: "year",
      label: w.meta_year_label || "Year",
      value: w.year,
    },
    w.meta_discipline_visible && w.category && {
      key: "category",
      label: w.meta_discipline_label || "Discipline",
      value: w.category,
    },
    services.length > 0 && {
      key: "services",
      label: "Services",
      value: services.join("\n"),
      multiline: true,
    },
  ].filter(Boolean);

  const showCaseStudyButton =
    !detailed && w.case_study_enabled && hasAnySection(w);

  return (
    <main ref={pageRef} data-testid="case-study" data-cs-slug={w.slug || ""} className="cs-page bg-[#0A0A0C]">
      {/* JSON-LD for AEO — CreativeWork + breadcrumb trail for this project */}
      <StructuredData id={`work-${w.slug}`} data={creativeWorkLD(w)} />
      <StructuredData
        id={`work-${w.slug}-crumb`}
        data={breadcrumbLD([
          { name: "Home", item: "/" },
          { name: "Work", item: "/work" },
          { name: w.title, item: `/work/${w.slug}` },
        ])}
      />
      {/* Page-level + per-project typography overrides */}
      <TypographyOverride pageId="case-study" scopeSelector=".cs-page" prefix="cs" />
      {w.slug && (
        <TypographyOverride
          pageId={`case-study:${w.slug}`}
          scopeSelector={`.cs-page[data-cs-slug="${w.slug}"]`}
          prefix="cs"
        />
      )}
      {/* Scroll progress bar — detailed case study only (1px gold) */}
      {detailed && (
        <motion.div
          data-testid="cs-scroll-progress"
          className="fixed top-0 left-0 right-0 h-px bg-[#C9A050] origin-left z-[60] pointer-events-none"
          style={{ scaleX: scrollYProgress }}
        />
      )}
      {/* HERO — full bleed cinematic open. Path A: no Framer scroll
         subscription, no per-frame opacity/scale — the video simply
         stays visible until you scroll past it. Zero JS work during
         scroll = zero Safari trackpad stutter. */}
      <section
        ref={heroRef}
        data-testid="cs-hero"
        className="relative h-[100svh] w-full overflow-hidden"
      >
        <div className="absolute inset-0">
          {w.video_url ? (
            <VimeoHero
              url={w.video_url}
              fallback={w.cover_image}
              orientation={w.orientation}
              settings={settings}
              onPlayerReady={(p) => { heroPlayerRef.current = p; }}
            />
          ) : (
            <picture className="absolute inset-0 w-full h-full">
              {w.cover_image_mobile && (
                <source media="(max-width: 767px)" srcSet={w.cover_image_mobile} />
              )}
              <img
                src={w.cover_image}
                alt={w.title}
                fetchpriority="high"
                decoding="async"
                width={1920}
                height={1080}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </picture>
          )}
        </div>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.0) 35%, rgba(0,0,0,0.0) 65%, rgba(0,0,0,0.85) 100%)",
          }}
        />
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-6 md:px-14 lg:px-24 pt-32 md:pt-40">
          <p className="overline text-white/85">{w.category} · {w.year}</p>
          <Link
            to="/work"
            data-testid="cs-back"
            data-cursor="View"
            className="overline text-white/85 hover:text-[#C9A050] transition-colors"
          >
            ← All work
          </Link>
        </div>
        <div className="absolute bottom-10 md:bottom-14 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
          <span className="overline text-[10px] text-white/60">Scroll</span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            className="block h-10 w-px bg-white/40"
          />
        </div>
      </section>

      {/* TITLE + META */}
      <section data-testid="cs-title-meta" className="px-6 md:px-14 lg:px-24 pt-32 md:pt-44 pb-24 md:pb-36 border-b border-white/5">
        <div className={`grid grid-cols-12 gap-6 md:gap-12 ${metaItems.length === 0 ? "" : ""}`}>
          <div className={metaItems.length === 0 ? "col-span-12" : "col-span-12 md:col-span-8"}>
            <Reveal>
              <h1
                data-testid="cs-title"
                className="cs-title font-display text-5xl sm:text-6xl md:text-7xl lg:text-[8rem] leading-[0.9] tracking-[-0.04em]"
              >
                {w.title}
                <span className="text-[#C9A050]">.</span>
              </h1>
            </Reveal>
            {w.subtitle && (
              <Reveal delay={0.1}>
                <p
                  data-testid="cs-subtitle"
                  className="cs-subtitle mt-10 md:mt-14 text-[#D4D4D8] text-xl md:text-2xl lg:text-3xl leading-[1.35] font-light max-w-3xl"
                >
                  {w.subtitle}
                </p>
              </Reveal>
            )}
          </div>
          {metaItems.length > 0 && (
            <aside className="col-span-12 md:col-span-4 md:pt-6 space-y-10 md:space-y-14" data-testid="cs-meta">
              {metaItems.map((m, i) => (
                <Reveal key={m.key} delay={0.15 + i * 0.05}>
                  <div data-testid={`cs-meta-${m.key}`}>
                    <p className="cs-section-label overline text-[#8A8A93] mb-2">{m.label}</p>
                    {m.multiline ? (
                      <ul className="space-y-1.5">
                        {m.value.split("\n").map((line, j) => (
                          <li key={j} className="cs-body">{line}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className={`cs-body ${m.gold ? "text-[#C9A050]" : ""}`}>
                        {m.value}
                      </p>
                    )}
                  </div>
                </Reveal>
              ))}
            </aside>
          )}
        </div>

        {/* Read full case study button (teaser only, when enabled) */}
        {showCaseStudyButton && (
          <div className="mt-20 md:mt-28">
            <Reveal>
              <Link
                to={`/work/${w.slug}/case-study`}
                data-testid="cs-read-full"
                data-cursor="Read"
                className="inline-flex items-center gap-3 overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-7 py-4 transition-all"
              >
                Read the full case study →
              </Link>
            </Reveal>
          </div>
        )}
      </section>

      {/* TEASER MODE: brief description (if any) */}
      {!detailed && w.description && (
        <section data-testid="cs-description" className="px-6 md:px-14 lg:px-24 py-24 md:py-36">
          <div className="grid grid-cols-12">
            <Reveal className="col-span-12 md:col-span-8 md:col-start-3">
              <p className="cs-body cs-body-large text-[#E4E4E7] font-light tracking-[-0.01em] whitespace-pre-line">
                {w.description}
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {/* DETAILED MODE: full storytelling sections */}
      {detailed && <DetailedSections w={w} />}

      {/* PULL QUOTE — both modes */}
      {w.pull_quote && (
        <section data-testid="cs-quote" className="px-6 md:px-14 lg:px-24 py-24 md:py-36 border-t border-white/5">
          <div className="grid grid-cols-12">
            <Reveal className="col-span-12 md:col-span-10 md:col-start-2">
              <blockquote className="cs-quote font-serif-accent text-[#C9A050] text-3xl md:text-5xl lg:text-6xl leading-[1.15] tracking-[-0.01em]">
                <span className="text-[#C9A050]/40 font-display">“</span>
                {w.pull_quote}
                <span className="text-[#C9A050]/40 font-display">”</span>
              </blockquote>
              {w.pull_quote_author && (
                <p className="mt-10 overline text-[#8A8A93]">— {w.pull_quote_author}</p>
              )}
            </Reveal>
          </div>
        </section>
      )}

      {/* GALLERY (legacy) */}
      {w.gallery?.length > 0 && (
        <section data-testid="cs-gallery" className="px-6 md:px-14 lg:px-24 py-24 md:py-36 border-t border-white/5">
          <div className="space-y-10 md:space-y-16 max-w-6xl mx-auto">
            {w.gallery.map((src, i) => (
              <Reveal key={i}>
                <img src={src} alt="" loading="lazy" className="w-full h-auto object-cover" />
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* NEXT PROJECT */}
      {next && next.slug && next.slug !== w.slug && (
        <NextProject next={next} />
      )}
    </main>
  );
}

/* ---------- Detailed mode — 10 storytelling sections ---------- */
function DetailedSections({ w }) {
  // Section 1 — The Client
  const client = w.cs_client_visible && (w.cs_client_name || w.cs_client_intro || w.client);
  // Section 2 — Context
  const context = w.cs_context_visible && w.cs_context_body;
  // Section 3 — Problem
  const problem = w.cs_problem_visible && w.cs_problem_body;
  // Section 4 — Insight
  const insight = w.cs_insight_visible && w.cs_insight_body;
  // Section 5 — Idea
  const idea = w.cs_idea_visible && w.cs_idea_body;
  // Section 6 — Execution
  const execution = w.cs_execution_visible && w.execution_blocks?.length > 0;
  // Section 7 — Key Moments
  const keyMoments = w.cs_keymoments_visible && (w.cs_keymoments_intro || w.key_moments?.length > 0);
  // Section 8 — Outcome
  const outcome = w.cs_outcome_visible && w.cs_outcome_body;
  // Section 9 — Closing
  const closing = w.cs_closing_visible && w.cs_closing_body;

  return (
    <>
      {client && (
        <SectionShell label={w.cs_client_label || "The Client"} testid="cs-section-client">
          {(w.cs_client_name || w.client) && (
            <h3 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] text-[#C9A050] mb-8 md:mb-12">
              {w.cs_client_name || w.client}
            </h3>
          )}
          {w.cs_client_intro && <BodyText>{w.cs_client_intro}</BodyText>}
        </SectionShell>
      )}
      {context && (
        <SectionShell label={w.cs_context_label || "The Situation"} testid="cs-section-context">
          <BodyText>{w.cs_context_body}</BodyText>
        </SectionShell>
      )}
      {problem && (
        <SectionShell label={w.cs_problem_label || "The Problem"} testid="cs-section-problem">
          <BodyText>{w.cs_problem_body}</BodyText>
        </SectionShell>
      )}
      {insight && (
        <SectionShell label={w.cs_insight_label || "The Insight"} testid="cs-section-insight">
          <BodyText>{w.cs_insight_body}</BodyText>
        </SectionShell>
      )}
      {idea && (
        <SectionShell label={w.cs_idea_label || "The Idea"} testid="cs-section-idea">
          <BodyText>{w.cs_idea_body}</BodyText>
        </SectionShell>
      )}
      {execution && (
        <SectionShell label={w.cs_execution_label || "Execution"} testid="cs-section-execution">
          <div className="space-y-20 md:space-y-32 max-w-6xl mx-auto">
            {w.execution_blocks.map((block, i) => (
              <Reveal key={i}>
                <ExecutionBlock block={block} />
              </Reveal>
            ))}
          </div>
        </SectionShell>
      )}
      {keyMoments && (
        <SectionShell label={w.cs_keymoments_label || "Key Moments"} testid="cs-section-keymoments">
          {w.cs_keymoments_intro && (
            <div className="mb-16 md:mb-24">
              <BodyText>{w.cs_keymoments_intro}</BodyText>
            </div>
          )}
          {w.key_moments?.length > 0 && (
            <div className="space-y-20 md:space-y-32 max-w-5xl mx-auto">
              {w.key_moments.map((m, i) => (
                <Reveal key={i}>
                  <KeyMoment moment={m} />
                </Reveal>
              ))}
            </div>
          )}
        </SectionShell>
      )}
      {outcome && (
        <SectionShell label={w.cs_outcome_label || "Outcome"} testid="cs-section-outcome">
          <BodyText>{w.cs_outcome_body}</BodyText>
        </SectionShell>
      )}
      {closing && (
        <SectionShell label={w.cs_closing_label || "Closing"} testid="cs-section-closing">
          <p className="cs-quote font-serif-accent text-[#E4E4E7] text-2xl md:text-3xl lg:text-4xl leading-[1.35] font-light max-w-4xl whitespace-pre-line">
            {w.cs_closing_body}
          </p>
        </SectionShell>
      )}
    </>
  );
}

/* ---------- Section shell (label + content + spacing) ---------- */
function SectionShell({ label, testid, children }) {
  return (
    <section
      data-testid={testid}
      className="px-6 md:px-14 lg:px-24 py-24 md:py-36 border-t border-white/5"
    >
      <Reveal>
        <p className="cs-section-label overline text-[#C9A050]/80 mb-10 md:mb-16">{label}</p>
      </Reveal>
      <Reveal delay={0.08}>
        <div className="grid grid-cols-12">
          <div className="col-span-12 md:col-span-10 md:col-start-2">{children}</div>
        </div>
      </Reveal>
    </section>
  );
}

function BodyText({ children }) {
  return (
    <p className="cs-body cs-body-medium text-[#E4E4E7] font-light tracking-[-0.005em] max-w-4xl whitespace-pre-line">
      {children}
    </p>
  );
}

/* ---------- Key moment block — title + body + optional media ---------- */
function KeyMoment({ moment }) {
  const kind = (moment.media_type || "").toLowerCase();
  const isVideo = kind === "video";
  const isAudio = kind === "audio";
  return (
    <article>
      {moment.media_url && (
        <figure className="mb-8 md:mb-10">
          <div className={`relative w-full overflow-hidden ${isVideo ? "aspect-video" : ""} bg-[#121214]`}>
            {isAudio ? (
              <div data-testid="cs-keymoment-audio" className="p-6 md:p-8 flex items-center gap-5 border border-[#C9A050]/15">
                <span aria-hidden className="flex-shrink-0 w-12 h-12 rounded-full border border-[#C9A050]/40 text-[#C9A050] flex items-center justify-center text-2xl leading-none">♪</span>
                <audio controls src={moment.media_url} className="flex-1 min-w-0" preload="metadata" />
              </div>
            ) : isVideo ? (
              <VimeoPlayer url={moment.media_url} fallback="" orientation="horizontal" premium respectMute testid="cs-keymoment-video" />
            ) : (
              <img src={moment.media_url} alt={moment.title || ""} loading="lazy" className="w-full h-auto object-cover" />
            )}
          </div>
        </figure>
      )}
      {moment.title && (
        <h4 className="font-display text-2xl md:text-3xl tracking-tight mb-4">
          {moment.title}
        </h4>
      )}
      {moment.body && (
        <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed max-w-3xl">
          {moment.body}
        </p>
      )}
    </article>
  );
}

/* ---------- Next project — title→detailed, anywhere else→teaser ---------- */
function NextProject({ next }) {
  const navigate = useNavigate();
  const teaserHref = `/work/${next.slug}`;
  const detailedHref = next.case_study_enabled ? `/work/${next.slug}/case-study` : teaserHref;

  const goTeaser = () => navigate(teaserHref);
  const goDetailed = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(detailedHref);
  };

  return (
    <section
      data-testid="cs-next"
      onClick={goTeaser}
      data-cursor="View"
      className="px-6 md:px-14 lg:px-24 py-28 md:py-44 border-t border-white/5 cursor-pointer group"
      role="link"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") goTeaser(); }}
    >
      <p className="overline text-[#8A8A93] mb-6 md:mb-8">Next project</p>
      <div className="grid grid-cols-12 gap-6 md:gap-12 items-center">
        <div className="col-span-12 md:col-span-7">
          <Link
            to={detailedHref}
            onClick={goDetailed}
            data-testid="cs-next-title"
            data-cursor={next.case_study_enabled ? "Read" : "View"}
            className="inline-block group/title"
          >
            <h2 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.92] tracking-[-0.035em] group-hover/title:text-[#C9A050] transition-colors duration-700">
              {next.title}
              <span className="text-[#C9A050]">.</span>
            </h2>
          </Link>
        </div>
        <div className="col-span-12 md:col-span-5">
          <div data-testid="cs-next-cover" className={`relative overflow-hidden ${next.orientation === "vertical" ? "aspect-[9/16] max-w-xs ml-auto" : "aspect-[4/3]"}`}>
            <img
              src={next.cover_image}
              alt={next.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Hero — responsive orientation-aware Vimeo player ---------- */
function VimeoHero({ url, fallback, orientation, settings, onPlayerReady }) {
  // Case-study hero honors the global mute state. background=0 lets the
  // Vimeo Player SDK actually flip the audio track when the user taps
  // the toggle. Chrome / iOS still require muted=1 on first load for
  // autoplay, so we start silent and unmute via the SDK on click.
  const { muted: globalMuted, toggle: toggleMute } = useAudioMute();
  const embedUrl = useMemo(
    () => (url ? normalizeVimeoEmbed(url, { background: false, muted: true }) : ""),
    [url]
  );
  const [loaded, setLoaded] = useState(false);
  const iframeRef = useRef(null);
  const sdkPlayerRef = useRef(null);
  const heroWrapRef = useRef(null);
  const isVertical = orientation === "vertical";
  const blurPx = settings?.work_side_blur ?? 20;
  const brightness = (settings?.work_side_brightness ?? 55) / 100;
  const blurClasses = isVertical ? "hidden md:block" : "block md:hidden";
  const frameClasses = isVertical
    ? "w-full h-full md:w-auto md:h-full md:aspect-[9/16]"
    : "w-full aspect-video md:w-full md:h-full md:aspect-auto";

  // Attach SDK on mount, apply initial mute state on ready.
  // iOS Safari note: setMuted(false) is silently rejected on a new page
  // context (SPA route change / reload) until a user gesture fires on THIS
  // page. Verify with getMuted() and arm a one-time gesture listener that
  // retries the unmute the moment the user taps anywhere on the page.
  useEffect(() => {
    if (!iframeRef.current || !embedUrl) return undefined;
    let cancelled = false;
    let p;
    let unmuteRetryCleanup = null;

    const armUnmuteRetry = () => {
      if (unmuteRetryCleanup) return;
      const retry = () => {
        if (!sdkPlayerRef.current || cancelled) return;
        sdkPlayerRef.current.setMuted(false).catch(() => {});
        sdkPlayerRef.current.setVolume(1).catch(() => {});
      };
      const opts = { once: true, capture: true };
      window.addEventListener("pointerdown", retry, opts);
      window.addEventListener("touchstart", retry, opts);
      window.addEventListener("keydown", retry, opts);
      unmuteRetryCleanup = () => {
        window.removeEventListener("pointerdown", retry, opts);
        window.removeEventListener("touchstart", retry, opts);
        window.removeEventListener("keydown", retry, opts);
      };
    };

    try {
      p = new VimeoPlayerSDK(iframeRef.current);
      sdkPlayerRef.current = p;
      p.ready().then(async () => {
        if (cancelled) return;
        setLoaded(true);
        await p.setMuted(globalMuted).catch(() => {});
        await p.setVolume(globalMuted ? 0 : 1).catch(() => {});
        if (!globalMuted) {
          const actuallyMuted = await p.getMuted().catch(() => true);
          if (actuallyMuted) armUnmuteRetry();
        }
        try { onPlayerReady && onPlayerReady(p); } catch {}
      }).catch(() => {});
    } catch {}
    return () => {
      cancelled = true;
      sdkPlayerRef.current = null;
      if (unmuteRetryCleanup) unmuteRetryCleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedUrl]);

  // Push mute state to SDK whenever it flips (no re-embed).
  useEffect(() => {
    const p = sdkPlayerRef.current;
    if (!p) return;
    p.setMuted(globalMuted).catch(() => {});
    p.setVolume(globalMuted ? 0 : 1).catch(() => {});
  }, [globalMuted]);

  // Visibility-based audio fade — IntersectionObserver-only (no rAF, no
  // per-frame getBoundingClientRect). IO with 51 fine thresholds gives
  // ~2% steps as the block scrolls in/out; the 0.02 delta gate on
  // setVolume drops that to ~50 iframe postMessages across the whole
  // fade — imperceptible in audio, but zero per-frame layout reads on
  // the main thread during scroll. This is what finally kills the
  // remaining scroll-up stutter on the case-study hero.
  useEffect(() => {
    const wrap = heroWrapRef.current;
    if (!wrap) return undefined;
    let lastApplied = -1;

    const applyVolumeIfChanged = (target) => {
      if (Math.abs(target - lastApplied) < 0.02) return;
      lastApplied = target;
      const p = sdkPlayerRef.current;
      try { p && p.setVolume(target); } catch {}
    };

    const thresholds = Array.from({ length: 51 }, (_, i) => i / 50);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const target = (!entry.isIntersecting || globalMuted) ? 0 : entry.intersectionRatio;
        applyVolumeIfChanged(target);
      });
    }, { threshold: thresholds });
    io.observe(wrap);

    return () => {
      io.disconnect();
    };
  }, [globalMuted]);

  return (
    <div ref={heroWrapRef} className="absolute inset-0 flex items-center justify-center bg-[#0A0A0C]">
      {fallback && (
        <div
          aria-hidden="true"
          data-testid="cs-hero-bg"
          className={`absolute inset-0 pointer-events-none ${blurClasses}`}
          style={{
            backgroundImage: `url('${fallback}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: `blur(${blurPx}px) saturate(0.95) brightness(${brightness})`,
            transform: "scale(1.1)",
            transformOrigin: "center",
          }}
        />
      )}
      <div
        className={`relative z-[1] overflow-hidden bg-black ${frameClasses}`}
        style={{
          // Pre-promote this narrow iframe wrapper to a compositor layer
          // so Safari doesn't pay the one-time "wake-up" cost when the
          // iframe first scrolls into viewport (that was the residual
          // stutter you were seeing on trackpad drag). Applied ONLY to
          // this small wrapper — not the parent 100svh motion.div, which
          // is what backfired earlier by creating a huge GPU layer.
          transform: "translate3d(0, 0, 0)",
          WebkitTransform: "translate3d(0, 0, 0)",
        }}
      >
        {/* Brand SpadeMark loader sits in the video area until Vimeo is ready */}
        <div
          aria-hidden="true"
          data-testid="cs-hero-loading"
          className="absolute inset-0 flex items-center justify-center bg-black"
          style={{
            opacity: loaded ? 0 : 1,
            transition: "opacity 700ms ease",
            pointerEvents: loaded ? "none" : "auto",
          }}
        >
          <SpadeMark size="compact" />
        </div>
        {embedUrl && (
          <iframe
            ref={iframeRef}
            title="case-study-hero"
            src={embedUrl}
            loading="eager"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            frameBorder="0"
            onLoad={() => setLoaded(true)}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ opacity: loaded ? 1 : 0, transition: "opacity 700ms ease" }}
          />
        )}
        {/* Inline audio mute toggle — bottom-LEFT of the video frame,
           high enough (bottom-20) to clear Vimeo's native chrome even
           though controls are hidden on this embed. z-[30] within the
           frame's stacking context sits above the iframe. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggleMute(); }}
          aria-label={globalMuted ? "Turn video sound on" : "Turn video sound off"}
          aria-pressed={!globalMuted}
          data-testid="cs-hero-audio-toggle"
          className={`absolute bottom-6 left-4 md:bottom-8 md:left-6 z-[30] w-11 h-11 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-md border pointer-events-auto ${
            globalMuted
              ? "bg-black/50 border-white/25 text-white/80 hover:text-white hover:border-white/60"
              : "bg-[#C9A050] border-[#C9A050] text-black hover:brightness-110"
          }`}
        >
          {globalMuted ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M11 5 6 9H2v6h4l5 4V5Z" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M11 5 6 9H2v6h4l5 4V5Z" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
          )}
        </button>
      </div>
    </div>
  );
}

/* ---------- Single execution block — large media + caption ---------- */
function ExecutionBlock({ block }) {
  const kind = (block.type || "").toLowerCase();
  if (kind === "audio") {
    return (
      <figure data-testid="cs-exec-audio" className="bg-[#121214] border border-[#C9A050]/15 rounded-md p-6 md:p-8 flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <span aria-hidden className="flex-shrink-0 w-11 h-11 rounded-full border border-[#C9A050]/40 text-[#C9A050] flex items-center justify-center text-xl leading-none">♪</span>
          <div className="flex-1 min-w-0">
            <p className="overline text-[#C9A050]/70 mb-1">Original composition</p>
            {block.title && (
              <p className="font-serif-accent text-xl md:text-2xl text-[#E4E4E7] leading-tight truncate">
                {block.title}
              </p>
            )}
          </div>
        </div>
        <audio controls src={block.url} className="w-full" preload="metadata" />
        {block.caption && (
          <figcaption className="text-sm md:text-base text-[#8A8A93] max-w-3xl leading-relaxed">
            {block.caption}
          </figcaption>
        )}
      </figure>
    );
  }
  const isVideo = kind === "video";
  return (
    <figure>
      <div className={`relative w-full overflow-hidden ${isVideo ? "aspect-video" : ""} bg-[#121214]`}>
        {isVideo ? (
          <VimeoPlayer url={block.url} fallback="" orientation="horizontal" premium respectMute testid="cs-exec" />
        ) : (
          <img src={block.url} alt={block.caption || ""} loading="lazy" className="w-full h-auto object-cover" />
        )}
      </div>
      {block.caption && (
        <figcaption className="mt-5 md:mt-6 text-sm md:text-base text-[#8A8A93] max-w-3xl leading-relaxed">
          {block.caption}
        </figcaption>
      )}
    </figure>
  );
}

/* ---------- Helpers ---------- */
function applyDefaults(w) {
  return {
    meta_client_visible: true,
    meta_year_visible: true,
    meta_discipline_visible: true,
    meta_client_label: "Client",
    meta_year_label: "Year",
    meta_discipline_label: "Discipline",
    cs_client_visible: true,
    cs_context_visible: true,
    cs_problem_visible: true,
    cs_insight_visible: true,
    cs_idea_visible: true,
    cs_execution_visible: true,
    cs_keymoments_visible: true,
    cs_outcome_visible: true,
    cs_closing_visible: true,
    case_study_enabled: false,
    ...w,
  };
}

function hasAnySection(w) {
  return !!(
    (w.cs_client_visible && (w.cs_client_name || w.cs_client_intro || w.client)) ||
    (w.cs_context_visible && w.cs_context_body) ||
    (w.cs_problem_visible && w.cs_problem_body) ||
    (w.cs_insight_visible && w.cs_insight_body) ||
    (w.cs_idea_visible && w.cs_idea_body) ||
    (w.cs_execution_visible && w.execution_blocks?.length > 0) ||
    (w.cs_keymoments_visible && (w.cs_keymoments_intro || w.key_moments?.length > 0)) ||
    (w.cs_outcome_visible && w.cs_outcome_body) ||
    (w.cs_closing_visible && w.cs_closing_body)
  );
}
